import React, { useCallback, useContext, useEffect } from 'react'
import { useHeaderTabs as useHeaderTabsNext } from '@e3dc-react/shell/Libs/Header'
import { ChargePowerSection, WeatherForecastSection } from 'src/Libs/smart-charge/components'
import { useIdlePeriodData, useSmartCharge } from 'src/Libs/smart-charge/hooks'
import { IdlePeriodLegacySettingsFormModel, IdlePeriodSettingsFormModel, SmartChargeSettingsFormModel } from 'src/Libs/smart-charge/models'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { WallboxLoadingPrioSettings } from 'src/Libs/wallboxes/components'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { SmartFunctionTabs } from 'src/Configuration/smart-functions'
import IdlePeriodSettingsDispatch from 'src/Libs/smart-charge/components/IdlePeriodSettingsDispatch'
import { useInstallationAccessRightsContext } from 'src/Libs/installation-rights-management/provider/InstallationAccessRightsContext'
import BannerQueue from 'src/Libs/banners/components/BannerQueue'
import { BannerLocations } from 'src/Libs/banners/types'
import { SmartChargeSettingsSkeleton } from 'src/Routes/SystemSettings/Settings/SystemSettingsSkeletons'
import useDynamicPricesSettings from 'src/Libs/dynamic-power-prices/hooks/use-dynamic-prices-settings'
import { useWallboxesLoadPrio } from 'src/Libs/wallboxes'
import { InstallationOnlineStatusContext } from 'src/Libs/installations/context/installation-online-status-context'
import { OfflineInstallationBlankView } from 'src/Libs/offline-mode'
import { OriginPage } from 'src/Libs/wallboxes/types'
import { useLocation } from 'react-router-dom'
import { useSnackbarMessageBasedOnSoftwareVersion } from 'src/Libs/home-power-station'
import { FeatureElements, getHaveElementsNewContent, useHasNewFeature } from 'src/Libs/new-content'
import { useNewRelic } from '@e3dc-react/shell/Libs/observability-tool'
import { SettingsRedirectHintCard } from 'src/Elements/Data'
import { getDynamicPowerPricesVersion, getSupportedSoftwareVersionsDynamicPowerPrices } from 'src/Libs/dynamic-power-prices/utils/price-settings'
import { useSystemInfoStorage } from 'src/Libs/system-data-handler/api/SystemDataFetching'
import { BlankViewImageVariants, BlankView } from 'src/Components/blank-view'
import useCheckInstallationLocation from 'src/Libs/dynamic-power-prices/hooks/use-check-installation-location'
import { usePowermeterList } from 'src/Libs/power-meters/hooks/use-power-meters'
import { isFlowTheme } from 'src/Libs/utils/theme-helpers'
import { useFarmContext } from 'src/Elements/Farm'
import { useIsTariffRedirectDisabled } from 'src/Libs/system-settings/hooks/use-is-tariff-redirect-disabled'

interface Props {
  installationId?: string
  storageId: string
}

const SmartChargePage: React.FC<Props> = ({ installationId, storageId }) => {
  const location = useLocation()
  const { pageActions } = useNewRelic()
  const { setActiveTab } = useHeaderTabsNext()
  const { t } = useCustomTranslation()
  const { currentFeatureContentVersion, handleUpdateContentVersion } = useHasNewFeature()
  const hasNewContent = getHaveElementsNewContent(currentFeatureContentVersion, [
    FeatureElements.PropertySmartFunctionSideBarElement,
    FeatureElements.PropertySmartFunctionsDynamicEmsFeature,
  ])

  const { isFarm, farmMaster } = useFarmContext()
  const currentStorageId = farmMaster?.storageId ?? storageId
  const {
    isLoading: isSmartChargeDataLoading,
    isError: isSmartChargeDataError,
    smartChargeSettings,
    updateSettings,
  } = useSmartCharge(currentStorageId)
  const installationAccessContext = useInstallationAccessRightsContext()

  const {
    dynamicPricesSettings,
    isLoading: isDppDataLoading,
    isError: isDppDataError,
  } = useDynamicPricesSettings({ storageId, origin: OriginPage.SmartCharge })

  const [powermeterHardwares] = usePowermeterList(currentStorageId)

  const {
    isLoading: isWallboxPrioDataLoading,
    isError: isWallboxPrioDataError,
    loadPrioSettings,
    updateLoadPrioSettings,
  } = useWallboxesLoadPrio(currentStorageId)

  const {
    isLoading: isIdlePeriodDataLoading,
    isError: isIdlePeriodDataError,
    settings: idlePeriodSettings,
    updateLegacySettings,
    updateDefaultSettings,
  } = useIdlePeriodData(currentStorageId)
  const { enqueueErrorStack, enqueueSuccessStack } = useSnackbar()

  const { isServerOnline } = useContext(InstallationOnlineStatusContext)
  const isServerOffline = isFarm && isServerOnline !== true
  const pageName = t('framework.smartFunctions.title')
  const weatherForecastAccordionName = t('systemSettings.smartFunctions.smartCharge.weatherForecast.sectionTitle')
  const idlePeriodSettingsDispatchAccordionName = t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.sectionTitle')
  const legacyIdlePeriodsAccordionName = t('systemSettings.smartFunctions.smartCharge.emsIdlePeriods.sectionTitle')
  const chargePowerSectionAccordionName = t('systemSettings.smartFunctions.smartCharge.chargePower.sectionTitle')
  const { handleDisplaySnackbarMessageBasedOnSoftwareVersion } = useSnackbarMessageBasedOnSoftwareVersion()

  const { systemInfo } = useSystemInfoStorage(storageId)
  const { countryCode } = useCheckInstallationLocation()
  const softwareVersion = systemInfo?.storage?.info?.sw_release
  const { doesSupportFullVersion, doesSupportMinimumVersion, doesSupportFullVersionOnlyGermany } =
    getSupportedSoftwareVersionsDynamicPowerPrices(softwareVersion)
  const hasPriceSettings = dynamicPricesSettings !== null
  const isTariffRedirectDisabled = useIsTariffRedirectDisabled()

  const { isFullVersionValid } = getDynamicPowerPricesVersion(
    doesSupportMinimumVersion,
    doesSupportFullVersion,
    doesSupportFullVersionOnlyGermany,
    hasPriceSettings,
    countryCode
  )

  useEffect(() => {
    if (hasNewContent) {
      handleUpdateContentVersion([FeatureElements.PropertySmartFunctionSideBarElement, FeatureElements.PropertySmartFunctionsDynamicEmsFeature])
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasNewContent])

  useEffect(() => {
    setActiveTab(SmartFunctionTabs.smartCharge)
  }, []) // eslint-disable-line

  const handleWeatherForecastUpdate = useCallback(
    async (values: SmartChargeSettingsFormModel) => {
      try {
        await updateSettings(values)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName: weatherForecastAccordionName, isEmsSetting: true })
      } catch (error) {
        console.error(error)
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName: weatherForecastAccordionName }))
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, handleDisplaySnackbarMessageBasedOnSoftwareVersion, updateSettings, t]
  )
  const handleIdlePeriodsUpdate = useCallback(
    async (values: IdlePeriodSettingsFormModel) => {
      try {
        await updateDefaultSettings(values)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({
          pageName,
          accordionName: idlePeriodSettingsDispatchAccordionName,
          isEmsSetting: true,
        })
      } catch (error) {
        console.error(error)
        enqueueErrorStack(
          t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName: idlePeriodSettingsDispatchAccordionName })
        )
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, handleDisplaySnackbarMessageBasedOnSoftwareVersion, updateSettings, t]
  )
  const handleLegacyIdlePeriodsUpdate = useCallback(
    async (values: IdlePeriodLegacySettingsFormModel) => {
      try {
        await updateLegacySettings(values)
        handleDisplaySnackbarMessageBasedOnSoftwareVersion({ pageName, accordionName: legacyIdlePeriodsAccordionName, isEmsSetting: true })
      } catch (error) {
        console.error(error)
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName: legacyIdlePeriodsAccordionName }))
        throw error
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, handleDisplaySnackbarMessageBasedOnSoftwareVersion, updateSettings, t]
  )
  const handleChargePowerUpdate = useCallback(
    async (values: SmartChargeSettingsFormModel) => {
      try {
        await updateSettings(values)
        enqueueSuccessStack(
          t(
            values.emsStartManualCharge === 0
              ? 'systemSettings.smartFunctions.smartCharge.chargePower.stopped'
              : 'systemSettings.smartFunctions.smartCharge.chargePower.update-successful',
            { pageName, accordionName: chargePowerSectionAccordionName }
          )
        )
      } catch (error) {
        console.error(error)
        enqueueErrorStack(t('common.systemSettings.api.error.failed-form-submission', { pageName, accordionName: chargePowerSectionAccordionName }))
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [enqueueErrorStack, enqueueSuccessStack, updateSettings, t]
  )

  const isDataLoading = isSmartChargeDataLoading || isIdlePeriodDataLoading || isDppDataLoading || isWallboxPrioDataLoading
  const isNoDataAvailable = smartChargeSettings === null && idlePeriodSettings === null && dynamicPricesSettings === null && loadPrioSettings === null

  useEffect(() => {
    if (location.state?.redirectedFromDppWidget) {
      pageActions.redirect({ redirectedFrom: 'DPPWidget', redirectedTo: 'SmartChargeDppSettings' })
    }
  }, [location.state?.redirectedFromDppWidget, pageActions])

  if (isFlowTheme) return null

  if (isDataLoading) return <SmartChargeSettingsSkeleton />

  if (isNoDataAvailable) {
    return (
      <BlankView
        title={'systemSettings.api.no-data-available.title'}
        explanation={'systemSettings.api.no-data-available.explanation'}
        imageVariant={
          isSmartChargeDataError || isIdlePeriodDataError || isDppDataError || isWallboxPrioDataError
            ? BlankViewImageVariants.Worried
            : BlankViewImageVariants.Neutral
        }
      />
    )
  }

  if (isServerOffline) {
    return (
      <OfflineInstallationBlankView
        titleKey="systemSettings.smartFunctions.smartCharge.server-offline-titel"
        explanationKey="systemSettings.smartFunctions.smartCharge.server-offline-explanation"
      />
    )
  }

  return (
    <>
      <BannerQueue location={BannerLocations.SettingsSmartCharge} sx={{ mb: 2 }} />
      {isFullVersionValid && (
        <SettingsRedirectHintCard
          title={t('dynamic-tariff-settings.title')}
          boxTitle={t('dynamic-tariff-settings.redirect.title')}
          buttonText={t('dynamic-tariff-settings.redirect.button-text')}
          redirectUrl={`/installation/${installationId}/${currentStorageId}/properties/tariffs`}
          isRedirectDisabled={isTariffRedirectDisabled}
          disabledTooltip={isTariffRedirectDisabled ? t('common.disabled-because-of-offline-system') : undefined}
        />
      )}
      <WeatherForecastSection
        model={smartChargeSettings}
        powermeterHardwares={powermeterHardwares ?? []}
        onChange={handleWeatherForecastUpdate}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
      />
      <IdlePeriodSettingsDispatch
        value={idlePeriodSettings}
        onUpdateDefault={handleIdlePeriodsUpdate}
        onUpdateLegacy={handleLegacyIdlePeriodsUpdate}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
      />
      <WallboxLoadingPrioSettings
        loadPrioSettings={loadPrioSettings}
        updateLoadPrioSettings={updateLoadPrioSettings}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
        origin={OriginPage.SmartCharge}
      />
      <SettingsRedirectHintCard
        title={t('systemSettings.price-based-charging.section-title')}
        boxTitle={t('systemSettings.price-based-charging.tariffs.redirect.title')}
        buttonText={t('systemSettings.price-based-charging.tariffs.redirect.button-text')}
        redirectUrl={`/installation/${installationId}/${currentStorageId}/properties/tariffs`}
        isRedirectDisabled={isTariffRedirectDisabled}
        disabledTooltip={isTariffRedirectDisabled ? t('common.disabled-because-of-offline-system') : undefined}
      />
      <ChargePowerSection
        model={smartChargeSettings}
        onChange={handleChargePowerUpdate}
        hasWritingRights={installationAccessContext.hasInstallationWritingRights}
      />
    </>
  )
}

export default SmartChargePage
